import { DocumentationContentType } from '@modules/clients/docSiteSearchType';
import { NavigationTypeRaw } from '@modules/utilities/pageBuild/types/NavigationRaw';
import {
  DocSiteSearchResponse,
  getTranslatedCategoryDisplayText,
  getCategoryTranslationLabels,
} from '@modules/clients/docSiteSearch';
import { isDeprecatedTag } from '@modules/apiReference/page/sharedUI/utils/tagUtils';
import { TCategorizedSearchResults, TSearchResult } from '../types/SearchResult';
import { TCategorizedSearchListItems, TSearchListItem } from '../types/SearchListItem';

export const processCreatorSearchResponse = (
  response: DocSiteSearchResponse,
  translate: (key: string, args?: Record<string, string>) => string,
): TSearchResult[] => {
  if (!response.results) {
    return [];
  }
  return response.results.map((result) => ({
    searchSessionId: response.searchSessionId || '',
    title: result.highlightedTitle || result.title || '',
    description: result.highlightedDisplayedSummary || result.displayedSummary || '',
    url: result.url || '',
    category: getTranslatedCategoryDisplayText(result, translate) || '',
    documentationContentType: result.documentationContentType,
    documentationSubType: result.documentationSubType,
    documentationThirdType: result.documentationThirdType,
    categoryTranslationLabels: getCategoryTranslationLabels(result),
    deprecated:
      ((result.documentationContentType === DocumentationContentType.LuaAPI ||
        result.documentationContentType === DocumentationContentType.CloudAPI) &&
        result.tags?.some(isDeprecatedTag)) ??
      false,
    isSemantic: response.isSemantic ?? false,
    createdAtUtc: result.createdAtUtc || '',
    updatedAtUtc: result.updatedAtUtc || '',
    views: result.views,
    clicks: result.clicks,
    author: result.author,
  }));
};

export interface ProcessedSearchData {
  resultsByContentType: TCategorizedSearchResults;
  searchListItems: TCategorizedSearchListItems;
  numberOfCategories: number;
  numberOfItems: number;
  maxItemsPerCategory?: number;
}

export const convertToSearchListItems = (results: TSearchResult[]): TSearchListItem[] => {
  return results.map((result) => {
    const contentType = result.documentationContentType;
    let type: NavigationTypeRaw;
    if (contentType === DocumentationContentType.LuaAPI) {
      type = NavigationTypeRaw.EngineAPI;
    } else if (contentType === DocumentationContentType.CloudAPI) {
      type = NavigationTypeRaw.CloudAPI;
    } else if (contentType === DocumentationContentType.DevForum) {
      type = NavigationTypeRaw.Forum;
    } else if (contentType === DocumentationContentType.Video) {
      type = NavigationTypeRaw.Videos;
    } else if (contentType === DocumentationContentType.Article) {
      type = NavigationTypeRaw.Markdown;
    } else {
      // Default to Markdown for unknown/null content types
      type = NavigationTypeRaw.Markdown;
    }

    return {
      ...result,
      translatedCategoryDisplayText: result.category,
      id: result.url,
      path: result.url,
      resultRef: result,
      type,
      createdAtUtc: result.createdAtUtc,
      updatedAtUtc: result.updatedAtUtc,
    };
  });
};

export const processSearchResults = (
  searchResults: TSearchResult[],
  isFilterDefault: boolean,
  maxResultsPerCategory = 3,
): ProcessedSearchData => {
  // Process search results by content type
  const allResultsByContentType =
    searchResults.length === 0
      ? ({} as TCategorizedSearchResults)
      : searchResults.reduce((acc, result) => {
          const contentType = result.documentationContentType ?? DocumentationContentType.Article;
          acc[contentType] = [...(acc[contentType] || []), result];
          return acc;
        }, {} as TCategorizedSearchResults);

  const numberOfCategories = Object.keys(allResultsByContentType).length;
  const maxItemsPerCategory =
    isFilterDefault && numberOfCategories > 1 ? maxResultsPerCategory : undefined;

  // limit cardinality of each category to maxResultsPerCategory
  const resultsByContentType = Object.fromEntries(
    Object.entries(allResultsByContentType).map(([contentType, allResults]) => [
      contentType,
      allResults.slice(0, maxItemsPerCategory),
    ]),
  ) as TCategorizedSearchResults;

  // Convert results to list items
  const searchListItems =
    Object.keys(resultsByContentType).length === 0
      ? ({} as TCategorizedSearchListItems)
      : (Object.keys(allResultsByContentType) as DocumentationContentType[]).reduce(
          (acc, contentType) => {
            const results = resultsByContentType[contentType];
            acc[contentType] = convertToSearchListItems(results);
            return acc;
          },
          {} as TCategorizedSearchListItems,
        );

  const numberOfItems = Object.values(searchListItems).length;

  return {
    resultsByContentType,
    searchListItems,
    numberOfCategories,
    numberOfItems,
  };
};
